import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What's Vijay's leadership experience?",
  "Show me his ROAS & CTR results",
  "Is he open to Creative Head roles?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm Vijay's AI assistant. Ask me anything about his 9+ years in design, his impact numbers, or why he's ready for a Creative Head role.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("vr_chat_sid") ||
      (() => {
        const s = crypto.randomUUID();
        localStorage.setItem("vr_chat_sid", s);
        return s;
      })()
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const appendToken = (t) =>
    setMessages((m) => {
      const c = [...m];
      c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t };
      return c;
    });

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = p.slice(6);
          if (d === "[DONE]") continue;
          try {
            appendToken(JSON.parse(d).t);
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: "Something went wrong — reach Vijay directly at kumar.edu12vk@gmail.com.",
        };
        return c;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <motion.button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className="fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full bg-[#FF4D23] text-[#0A0B0E] flex items-center justify-center shadow-[0_8px_30px_rgba(255,77,35,0.45)]"
        aria-label="Open AI assistant"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[70] w-[92vw] max-w-sm h-[480px] bg-[#12141B]/95 backdrop-blur-xl border border-[#222634] rounded-2xl flex flex-col overflow-hidden shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-[#222634] flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#FF4D23]/15 border border-[#FF4D23]/40 flex items-center justify-center">
                <Sparkles size={15} className="text-[#FF4D23]" />
              </span>
              <div>
                <p className="font-display font-bold text-sm">Ask Vijay's AI</p>
                <p className="text-[10px] font-mono2 tracking-widest uppercase text-[#8A92A3]">Powered by GPT 5.4</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3" data-lenis-prevent data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#FF4D23] text-[#0A0B0E] font-medium rounded-br-md"
                        : "bg-[#1A1D27] text-[#F5F6FA] border border-[#222634] rounded-bl-md"
                    }`}
                  >
                    {m.content}
                    {m.role === "assistant" && m.content === "" && (
                      <span className="inline-block w-2 h-4 bg-[#FF4D23] animate-pulse rounded-sm" />
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="ai-chat-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`ai-chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                    onClick={() => send(s)}
                    className="text-[11px] font-mono2 text-[#B6BDCB] border border-[#3B4254] rounded-full px-3 py-1.5 hover:border-[#FF4D23] hover:text-[#F5F6FA] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-[#222634] flex gap-2"
            >
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Vijay's work..."
                className="flex-1 bg-[#0A0B0E] border border-[#3B4254] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF4D23] transition-colors"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="ai-chat-send"
                className="w-10 h-10 rounded-full bg-[#FF4D23] text-[#0A0B0E] flex items-center justify-center disabled:opacity-50 hover:bg-[#ff6a45] transition-colors"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
